import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAdminById, updateAdmin, syncAdminRoles, getRoles } from '../api';
import './AdminDetail.css';

const getAdminRoleLabel = (admin = {}) => {
  const rawRole = admin.role || (admin.is_super_admin === 1 ? 'super_admin' : 'admin');
  const role = String(rawRole || 'admin').toLowerCase();
  return role.includes('super') ? 'Super Admin' : 'Admin';
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const getInitials = (name = '') => {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join('');
  return initials || 'AD';
};

const emptyFormData = {
  firstname: '',
  lastname: '',
  email: '',
  phone_number: '',
  password: '',
  confirmPassword: '',
};

const INFO_ICONS = {
  email: '✉️',
  phone: '📞',
  created: '🗓️',
  updated: '🕓',
};

export default function AdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [admin, setAdmin] = useState(null);

  const [allRoles, setAllRoles] = useState([]);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [formData, setFormData] = useState(emptyFormData);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  const fetchAdmin = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAdminById(id);
      setAdmin(data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch admin details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    getRoles()
      .then((data) => setAllRoles(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to load roles:', err));
  }, []);

  const fullName = admin?.full_name || `${admin?.firstname || ''} ${admin?.lastname || ''}`.trim() || 'Unknown Admin';
  const roles = Array.isArray(admin?.roles) ? admin.roles : [];
  const permissions = Array.isArray(admin?.permissions_list) ? admin.permissions_list : [];
  const status = admin?.is_active === 0 || admin?.is_suspended === 1 ? 'Inactive/Suspended' : 'Active';

  const startEditing = () => {
    setFormData({
      firstname: admin?.firstname || '',
      lastname: admin?.lastname || '',
      email: admin?.email || '',
      phone_number: admin?.phone_number || '',
      password: '',
      confirmPassword: '',
    });
    setSelectedRoleIds(roles.map((role) => role.id));
    setSaveError('');
    setSaveSuccess('');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setSaveError('');
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleRole = (roleId) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]
    );
  };

  const handleSave = async () => {
    setSaveError('');

    if (!formData.firstname.trim() || !formData.lastname.trim() || !formData.email.trim()) {
      setSaveError('First name, last name, and email are required.');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setSaveError('Password must be at least 6 characters.');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      setSaveError('Passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        email: formData.email.trim(),
        phone_number: formData.phone_number || null,
      };
      if (formData.password) {
        payload.password = formData.password;
      }

      await updateAdmin(id, payload);

      const originalRoleIds = roles.map((role) => role.id);
      const rolesChanged =
        originalRoleIds.length !== selectedRoleIds.length ||
        !originalRoleIds.every((roleId) => selectedRoleIds.includes(roleId));

      if (rolesChanged) {
        if (selectedRoleIds.length === 0) {
          setSaveError('An admin must have at least one role. Details were saved, but roles were not changed.');
        } else {
          await syncAdminRoles(id, selectedRoleIds, 'sync');
        }
      }

      await fetchAdmin();
      setEditing(false);
      setSaveSuccess('Admin updated successfully.');
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to update admin.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-detail-page">
        <div className="admin-detail-loading-state">
          <div className="spinner"></div>
          <p>Loading admin details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-detail-page">
        <button className="back-button" onClick={() => navigate(-1)}>← Back</button>
        <div className="admin-detail-error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-detail-page">
      <div className="admin-detail-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>← Back</button>
        {!editing && (
          <button className="edit-admin-button" onClick={startEditing}>
            Edit Admin
          </button>
        )}
      </div>

      {saveSuccess && <div className="admin-detail-success-banner">{saveSuccess}</div>}

      <div className="admin-detail-page-card">
        <div className="admin-detail-page-top">
          <div className="admin-detail-avatar">{getInitials(fullName)}</div>
          <div>
            <h2>{fullName}</h2>
            <div className="admin-detail-meta-row">
              <span className={`role-badge ${getAdminRoleLabel(admin).toLowerCase().replace(' ', '-')}`}>{getAdminRoleLabel(admin)}</span>
              <span className={`status-badge ${status.toLowerCase().includes('inactive') ? 'suspended' : 'active'}`}>{status}</span>
            </div>
          </div>
        </div>

        {saveError && <div className="admin-detail-inline-error-banner">{saveError}</div>}

        {editing ? (
          <>
            <div className="admin-detail-edit-grid">
              <div>
                <label htmlFor="admin-firstname">First Name</label>
                <input
                  id="admin-firstname"
                  type="text"
                  value={formData.firstname}
                  onChange={(e) => handleFieldChange('firstname', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="admin-lastname">Last Name</label>
                <input
                  id="admin-lastname"
                  type="text"
                  value={formData.lastname}
                  onChange={(e) => handleFieldChange('lastname', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="admin-email">Email</label>
                <input
                  id="admin-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="admin-phone">Phone</label>
                <input
                  id="admin-phone"
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                />
              </div>
            </div>

            <div className="admin-detail-password-section">
              <h3>Change Password</h3>
              <p className="admin-access-empty">Leave blank to keep the current password.</p>
              <div className="admin-detail-edit-grid">
                <div>
                  <label htmlFor="admin-password">New Password</label>
                  <input
                    id="admin-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label htmlFor="admin-password-confirm">Confirm New Password</label>
                  <input
                    id="admin-password-confirm"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>

            <div className="admin-access-section">
              <div className="admin-access-card">
                <h3>Roles</h3>
                {allRoles.length > 0 ? (
                  <div className="chip-list">
                    {allRoles.map((role) => {
                      const active = selectedRoleIds.includes(role.id);
                      return (
                        <button
                          key={role.id}
                          type="button"
                          className={`chip chip-toggle ${active ? 'chip-role active' : ''}`}
                          onClick={() => toggleRole(role.id)}
                        >
                          {active && <span className="chip-check">✓</span>}
                          {role.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="admin-access-empty">No roles available.</p>
                )}
              </div>

              <div className="admin-access-card">
                <h3>Permissions</h3>
                {permissions.length > 0 ? (
                  <div className="chip-list">
                    {permissions.map((permission) => (
                      <span key={permission} className="chip chip-permission">{permission || 'Unnamed permission'}</span>
                    ))}
                  </div>
                ) : (
                  <p className="admin-access-empty">No permissions assigned.</p>
                )}
              </div>
            </div>

            <div className="admin-detail-edit-actions">
              <button className="btn-cancel-edit" onClick={cancelEditing} disabled={saving}>
                Cancel
              </button>
              <button className="btn-save-edit" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="admin-info-cards">
              <div className="admin-info-card">
                <span className="admin-info-icon">{INFO_ICONS.email}</span>
                <div>
                  <label>Email</label>
                  <span>{admin?.email || 'N/A'}</span>
                </div>
              </div>
              <div className="admin-info-card">
                <span className="admin-info-icon">{INFO_ICONS.phone}</span>
                <div>
                  <label>Phone</label>
                  <span>{admin?.phone_number || 'N/A'}</span>
                </div>
              </div>
              <div className="admin-info-card">
                <span className="admin-info-icon">{INFO_ICONS.created}</span>
                <div>
                  <label>Created At</label>
                  <span>{formatDate(admin?.created_at)}</span>
                </div>
              </div>
              <div className="admin-info-card">
                <span className="admin-info-icon">{INFO_ICONS.updated}</span>
                <div>
                  <label>Updated At</label>
                  <span>{formatDate(admin?.updated_at)}</span>
                </div>
              </div>
            </div>

            <div className="admin-access-section">
              <div className="admin-access-card">
                <h3>Roles</h3>
                {roles.length > 0 ? (
                  <div className="chip-list">
                    {roles.map((role) => (
                      <span key={role.id || role.name} className="chip chip-role">{role.name || 'Unnamed role'}</span>
                    ))}
                  </div>
                ) : (
                  <p className="admin-access-empty">No roles assigned.</p>
                )}
              </div>

              <div className="admin-access-card">
                <h3>Permissions</h3>
                {permissions.length > 0 ? (
                  <div className="chip-list">
                    {permissions.map((permission) => (
                      <span key={permission} className="chip chip-permission">{permission || 'Unnamed permission'}</span>
                    ))}
                  </div>
                ) : (
                  <p className="admin-access-empty">No permissions assigned.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
